import { getName } from '../app/functions.web';
import { isSuboptimalBrowser } from '../base/environment';
import { i18next, translateToHTML } from '../base/i18n';
import { getLocalParticipant } from '../base/participants';
import { toState } from '../base/redux';
import { getBackendSafePath, getMeetHourGlobalNS } from '../base/util';
import { getVpaasBillingId } from '../billing-counter/functions';
import { showWarningNotification } from '../notifications';
import { createRnnoiseProcessorPromise } from '../rnnoise';

import {
    getDashBoardUrlFromState,
    getSubscriptionPageFromState
} from './functions.any';

export * from './functions.any';

declare var APP: object;

/**
 * Web-side helpers retain the legacy `APP.store`-based signature so existing
 * call sites (Conference.js, MeetingLimitExceededDialog, etc.) continue to
 * work unchanged. They simply forward to the shared helpers that read from
 * redux state.
 */
export const getDashBoardUrl = () => getDashBoardUrlFromState(APP.store.getState());

export const getSubscriptionPage = () => getSubscriptionPageFromState(APP.store.getState());

export const hangUpReactNative = () => () => undefined;

/**
 * Returns the result of getWiFiStats from the global NS or does nothing
(returns empty result).
 * Fixes a concurrency problem where we need to pass a function when creating
 * a MHConference, but that method is added to the context later.
 *
 * @returns {Promise}
 * @private
 */
const getWiFiStatsMethod = () => {
    const gloabalNS = getMeetHourGlobalNS();

    return gloabalNS.getWiFiStats ? gloabalNS.getWiFiStats() : Promise.resolve('{}');
};

/**
 * Shows the suboptimal experience notification if needed.
 *
 * @param {Function} dispatch - The dispatch method.
 * @param {Function} t - The translation function.
 * @returns {void}
 */
export function maybeShowSuboptimalExperienceNotification(dispatch, t) {
    if (isSuboptimalBrowser()) {
        dispatch(
            showWarningNotification(
                {
                    titleKey: i18next.t('notify.suboptimalExperienceTitle'),
                    description: translateToHTML(
                        t,
                        i18next.t('notify.suboptimalBrowserWarning'),
                        {
                            recommendedBrowserPageLink: `${window.location.origin}/static/recommendedBrowsers.html`
                        }
                    )
                }
            )
        );
    }
}

/**
 * Returns an object aggregating the conference options.
 *
 * @param {Object|Function} stateful - The redux store state.
 * @returns {Object} - Options object.
 */
export function getConferenceOptions(stateful) {
    const state = toState(stateful);

    const options = state['features/base/config'];
    const { locationURL } = state['features/base/connection'];
    const { tenant } = state['features/base/jwt'];

    const { email, name: nick } = getLocalParticipant(state);

    if (tenant) {
        options.siteID = tenant;
    }

    if (options.enableDisplayNameInStats && nick) {
        options.statisticsDisplayName = nick;
    }

    if (options.enableEmailInStats && email) {
        options.statisticsId = email;
    }

    if (locationURL) {
        options.confID = `${locationURL.host}${getBackendSafePath(locationURL.pathname)}`;
    }

    options.applicationName = getName();
    options.getWiFiStatsMethod = getWiFiStatsMethod;
    options.createVADProcessor = createRnnoiseProcessorPromise;
    options.billingId = getVpaasBillingId(state);

    // Disable CallStats, if requessted.
    if (options.disableThirdPartyRequests) {
        delete options.callStatsID;
        delete options.callStatsSecret;
        delete options.getWiFiStatsMethod;
    }

    return options;
}
